import React, { memo, useContext, useMemo, useCallback } from 'react'

import { arrayOf, bool, string } from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { trackSettingsInteraction } from 'Pods/ThreeDStudio/Services/studioTracker'
import ButtonGroupSelector from 'Pods/ThreeDStudio/Shared/ButtonGroupSelector/ButtonGroupSelector'
import { getHiddenProviders } from 'Pods/ThreeDStudio/Shared/Utils/getHiddenProviders'
import { providerSupportsAction } from 'Pods/ThreeDStudio/Shared/Utils/providerSupportsAction'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { resetSettingsUnsupportedByProvider } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { setEnhancedProvider, setGenerationProvider } from 'Pods/ThreeDStudio/Store/actions/schema'
import { PROVIDER } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { PROVIDER_LABELS } from './providerLabels'

// `hiddenProviders` carries the per-action exclusions the caller knows about;
// kill-switch flags are read straight from the page context instead, so a flow
// that passes no list (retexture, model generation) is covered too and a new tab
// cannot forget them. getHiddenProviders returns a module-level constant, so it
// stays referentially stable across renders.
function AIEngineSelector({ isEnhanced = false, hiddenProviders = EMPTY_ARRAY }) {
  const t = useTranslation()
  const { enableHunyuanProvider } = useContext(ThreeDStudioPageContext)
  const flaggedOffProviders = getHiddenProviders({ enableHunyuanProvider })
  const generationAction = useThreeDStudioStore(state => state.schema.generationAction)
  const generationProvider = useThreeDStudioStore(state => state.schema.generationProvider)
  const enhancedProvider = useThreeDStudioStore(state => state.schema.enhancedProvider)
  const availableGenerationProviders = useThreeDStudioStore(
    state => state.schema.availableGenerationProviders
  )
  const availableEnhancedProviders = useThreeDStudioStore(
    state => state.schema.availableEnhancedProviders
  )
  const provider = isEnhanced ? enhancedProvider : generationProvider
  const availableProviders = isEnhanced ? availableEnhancedProviders : availableGenerationProviders

  const setProvider = isEnhanced ? setEnhancedProvider : setGenerationProvider
  const handleProviderClick = useCallback((id) => {
    trackSettingsInteraction('ai_engine', id)
    // The engine outranks the additional settings on the generation flow, so the
    // settings it cannot run are reset before it becomes active - otherwise the
    // schema resolved for it stays ineligible and the store rolls the engine back.
    if (!isEnhanced) {
      resetSettingsUnsupportedByProvider(id)
    }
    setProvider(id)
  }, [isEnhanced, setProvider])
  const handleHunyuanClick = useCallback(() => handleProviderClick(PROVIDER.TENCENT), [handleProviderClick])
  const handleMeshyClick = useCallback(() => handleProviderClick(PROVIDER.MESHY), [handleProviderClick])
  const handleTripo3DClick = useCallback(() => handleProviderClick(PROVIDER.TRIPO3D), [handleProviderClick])

  // On the generation flow an engine is only disabled when it cannot run the action at
  // all: the additional settings never disable it, picking it resets them instead. The
  // enhanced flow keeps the schema-driven list, where the engine *is* the mode
  // (Meshy = standard remesh, Hunyuan/Tripo3D = smart low poly).
  const isProviderDisabled = useCallback((id) => {
    if (!isEnhanced) {
      return !providerSupportsAction(generationAction, id)
    }

    return !availableProviders?.includes(id)
  }, [isEnhanced, generationAction, availableProviders])

  const buttons = useMemo(() => {
    const providerConfig = [
      { id: PROVIDER.TENCENT, onClick: handleHunyuanClick },
      { id: PROVIDER.MESHY, onClick: handleMeshyClick },
      { id: PROVIDER.TRIPO3D, onClick: handleTripo3DClick },
    ]

    return providerConfig
      .filter(({ id }) => !hiddenProviders.includes(id) && !flaggedOffProviders.includes(id))
      .map(({ id, onClick }) => {
        const disabled = isProviderDisabled(id)

        return {
          id,
          label: PROVIDER_LABELS[id],
          onClick,
          disabled,
          isActive: provider === id,
          tooltipMessage: disabled ? t('shared.aiEngine.disabledTooltip') : undefined,
        }
      })
  }, [
    t,
    provider,
    hiddenProviders,
    flaggedOffProviders,
    isProviderDisabled,
    handleHunyuanClick,
    handleMeshyClick,
    handleTripo3DClick,
  ])

  // With a single provider left there is nothing to choose from - the store
  // auto-selects it (setAvailableEnhancedProviders 0/1/many rules), so the
  // selector is hidden entirely.
  if (buttons.length <= 1) {
    return null
  }

  return (
    <ButtonGroupSelector
      buttons={buttons}
      keyName="provider"
      sliceName="schema"
      label={t('shared.aiEngine.label')}
    />
  )
}

AIEngineSelector.propTypes = {
  isEnhanced: bool,
  hiddenProviders: arrayOf(string),
}

export default memo(AIEngineSelector)
